import { useEffect, useState, useMemo } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PLATFORM_META } from "@/components/postMeta";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const { activeId } = useWorkspace();
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    if (!activeId) return;
    api.get(`/calendar?workspace_id=${activeId}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [activeId]);

  const byDay = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      if (!p.scheduled_at) return;
      const key = new Date(p.scheduled_at).toDateString();
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [posts]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const today = new Date().toDateString();

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground mt-2">Your scheduled and published posts at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))}><CaretLeft size={16} /></Button>
          <span className="font-heading font-bold text-lg w-40 text-center" data-testid="cal-month">
            {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          </span>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))}><CaretRight size={16} /></Button>
          <Button variant="ghost" data-testid="cal-today" onClick={() => setCursor(new Date())}>Today</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border">
          {DOW.map((d) => (
            <div key={d} className="p-3 text-xs font-mono uppercase text-muted-foreground text-center border-r border-border last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const key = date?.toDateString();
            const dayPosts = key ? byDay[key] || [] : [];
            const isToday = key === today;
            return (
              <div
                key={i}
                data-testid={date ? `cal-day-${date.getDate()}` : `cal-empty-${i}`}
                className={`min-h-[110px] p-2 border-r border-b border-border last:border-r-0 ${date ? "" : "bg-muted/30"}`}
              >
                {date && (
                  <>
                    <div className={`text-xs font-mono mb-1.5 w-6 h-6 flex items-center justify-center rounded ${isToday ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayPosts.slice(0, 3).map((p) => {
                        const meta = PLATFORM_META[p.platform];
                        return (
                          <div key={p.id} data-testid={`cal-post-${p.id}`}
                            className="flex items-center gap-1.5 px-1.5 py-1 rounded text-xs border border-border bg-background"
                            style={{ borderLeftColor: meta.color, borderLeftWidth: 3 }}>
                            <meta.icon size={12} weight="fill" style={{ color: meta.color }} />
                            {p.language && (
                              <span className="text-[9px] font-bold uppercase px-1 rounded bg-primary/15 text-primary" data-testid={`cal-lang-${p.id}`}>{p.language}</span>
                            )}
                            <span className="truncate">{p.body.slice(0, 24)}</span>
                          </div>
                        );
                      })}
                      {dayPosts.length > 3 && <p className="text-xs text-muted-foreground pl-1">+{dayPosts.length - 3} more</p>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {posts.length === 0 && (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground justify-center">
          <CalendarBlank size={18} /> No scheduled posts yet — schedule posts from the Studio.
        </div>
      )}
    </AppLayout>
  );
}
